/**
 * Stage Proof Integrity Guard
 * ─────────────────────────────────────────────────────────────────────────
 * `currentStage` is DESCRIPTIVE only. It is never proof that a gate passed.
 *
 * This module asserts that, for every job whose `currentStage` claims to be
 * past a given gate, the canonical proof fields for that gate are actually
 * satisfied. When stage and proof contradict, the job is reported as
 * INCOHERENT — and downstream readiness/dispatch logic must block it.
 *
 * Integrity guard only — does NOT:
 *   • mutate jobs or seed data
 *   • add admin overrides
 *   • change workflow stages or labels
 *   • create a new readiness model
 *
 * Consumed by:
 *   • src/data/readiness.ts (folded into install readiness)
 *   • surfaces that show install/dispatch readiness
 */

import type { Job, JobStage } from "./types";
import type { BuildReviewEvent } from "./buildReviewTypes";
import { deriveBuildReviewStatus } from "./buildReviewTypes";
import { migrationBypasses } from "./import/migrationVisibilityGuard";

/* ────────────────────────────────────────────────────────────────────────
   Stage → required-proof mapping
   ──────────────────────────────────────────────────────────────────────── */

/** Stages that imply Build Review must be approved. */
const BUILD_REVIEW_REQUIRED_STAGES: JobStage[] = [
  "Ready for Ordering",
  "Ordered",
  "Permit and Order Pending",
  "Ready for Scheduling",
  "Scheduled Tentative",
  "Scheduled Confirmed",
  "Ready for Dispatch",
  "Available for Contractor Assignment",
  "Assigned and Dispatched",
  "In Progress",
  "QA Review",
  "Completed",
];

/** Stages that imply materials are at least Ordered. */
const MATERIALS_ORDERED_REQUIRED_STAGES: JobStage[] = [
  "Ordered",
  "Permit and Order Pending",
  "Ready for Scheduling",
  "Scheduled Tentative",
  "Scheduled Confirmed",
  "Ready for Dispatch",
  "Available for Contractor Assignment",
  "Assigned and Dispatched",
  "In Progress",
  "QA Review",
  "Completed",
];

/** Stages that imply materials have been physically received/staged. */
const MATERIALS_RECEIVED_REQUIRED_STAGES: JobStage[] = [
  "Ready for Scheduling",
  "Scheduled Tentative",
  "Scheduled Confirmed",
  "Ready for Dispatch",
  "Available for Contractor Assignment",
  "Assigned and Dispatched",
  "In Progress",
  "QA Review",
  "Completed",
];

/** Stages that imply QA was submitted by the installer. */
const QA_SUBMITTED_REQUIRED_STAGES: JobStage[] = ["QA Review", "Completed"];

/* ────────────────────────────────────────────────────────────────────────
   Proof checks (canonical fields only — no stage inference allowed here)
   ──────────────────────────────────────────────────────────────────────── */

function isBuildReviewApproved(
  job: Job,
  buildReviewEvents: BuildReviewEvent[],
): boolean {
  const events = buildReviewEvents.filter((e) => e.jobId === job.id);
  if (events.length > 0) {
    return deriveBuildReviewStatus(events) === "Approved";
  }
  // Legacy proof flags — explicit approvals only.
  if (job.buildReviewStatus === "approved") return true;
  if (job.buildReviewApproved === true) return true;
  if (job.intakeApproved === true) return true;
  if (job.approvedForOrdering === true) return true;
  return false;
}

function isMaterialsOrdered(job: Job): boolean {
  const r = job.materialReadiness;
  return (
    r === "Ordered" ||
    r === "Partially Received" ||
    r === "Fully Received" ||
    r === "Staged for Job" ||
    r === "Loaded for Crew"
  );
}

function isMaterialsReceived(job: Job): boolean {
  const r = job.materialReadiness;
  return (
    r === "Fully Received" ||
    r === "Staged for Job" ||
    r === "Loaded for Crew"
  );
}

function isQaSubmitted(job: Job): boolean {
  return job.qaSubmitted === true;
}

/* ────────────────────────────────────────────────────────────────────────
   Public API
   ──────────────────────────────────────────────────────────────────────── */

export interface StageProofViolation {
  gate: "build_review" | "materials_ordered" | "materials_received" | "qa_submitted";
  reason: string;
}

export interface StageProofIntegrityResult {
  ok: boolean;
  violations: StageProofViolation[];
  /** Single short reason suitable for badges / inline UI. */
  primaryReason?: string;
}

/**
 * Assert that a job's currentStage is backed by the required proof fields.
 * Pure function — no side effects, no mutation.
 */
export function assertStageProofIntegrity(
  job: Job,
  buildReviewEvents: BuildReviewEvent[] = [],
): StageProofIntegrityResult {
  const violations: StageProofViolation[] = [];

  if (
    BUILD_REVIEW_REQUIRED_STAGES.includes(job.currentStage) &&
    !migrationBypasses(job, "build_review") &&
    !isBuildReviewApproved(job, buildReviewEvents)
  ) {
    violations.push({
      gate: "build_review",
      reason: `Stage "${job.currentStage}" requires Build Review approval, but no approval proof exists.`,
    });
  }

  if (
    MATERIALS_ORDERED_REQUIRED_STAGES.includes(job.currentStage) &&
    !migrationBypasses(job, "ordering") &&
    !isMaterialsOrdered(job)
  ) {
    violations.push({
      gate: "materials_ordered",
      reason: `Stage "${job.currentStage}" requires materials to be Ordered, but materialReadiness is "${job.materialReadiness}".`,
    });
  }

  if (
    MATERIALS_RECEIVED_REQUIRED_STAGES.includes(job.currentStage) &&
    !migrationBypasses(job, "ordering") &&
    !isMaterialsReceived(job)
  ) {
    violations.push({
      gate: "materials_received",
      reason: `Stage "${job.currentStage}" requires materials Received/Staged, but materialReadiness is "${job.materialReadiness}".`,
    });
  }

  if (
    QA_SUBMITTED_REQUIRED_STAGES.includes(job.currentStage) &&
    !isQaSubmitted(job)
  ) {
    violations.push({
      gate: "qa_submitted",
      reason: `Stage "${job.currentStage}" requires QA submission, but qaSubmitted is false.`,
    });
  }

  return {
    ok: violations.length === 0,
    violations,
    primaryReason: violations[0]?.reason,
  };
}

/** Convenience: boolean form for guard call sites. */
export function hasStageProofIntegrity(
  job: Job,
  buildReviewEvents: BuildReviewEvent[] = [],
): boolean {
  return assertStageProofIntegrity(job, buildReviewEvents).ok;
}
